/**
 * Componente AlertsSection
 * 
 * Seção de alertas do dashboard.
 * Agrupa todos os alertas operacionais em uma seção organizada e responsiva.
 * 
 * @module components/Dashboard/AlertsSection/AlertsSection
 */

import React from 'react';
import PropTypes from 'prop-types';
import PontoDesviosAlert from './PontoDesviosAlert';
import EstoqueAlertasAlert from './EstoqueAlertasAlert';
import AlertSkeleton from './AlertSkeleton';

/**
 * Componente de Seção de Alertas
 * 
 * Renderiza uma seção com todos os alertas operacionais do dashboard:
 * - Desvios de Ponto Recentes
 * - Alertas Críticos de Farmácia (Estoque/Validade)
 * 
 * @param {object} props - Propriedades do componente
 * @param {Array} props.pontoDesvios - Dados de desvios de ponto
 * @param {boolean} props.isLoadingPontoDesvios - Loading de desvios de ponto
 * @param {Error|string|null} props.errorPontoDesvios - Erro de desvios de ponto
 * @param {Array} props.estoqueAlertas - Dados de alertas de estoque
 * @param {boolean} props.isLoadingEstoqueAlertas - Loading de alertas de estoque
 * @param {Error|string|null} props.errorEstoqueAlertas - Erro de alertas de estoque
 * @param {string} [props.pontoTolerancia] - Tolerância de atraso para desvios de ponto
 * @param {string} [props.className] - Classes CSS adicionais
 * 
 * @returns {JSX.Element} Componente de seção de alertas
 * 
 * @example
 * ```jsx
 * <AlertsSection
 *     pontoDesvios={pontoDesviosData}
 *     isLoadingPontoDesvios={isLoading}
 *     errorPontoDesvios={error}
 *     estoqueAlertas={estoqueAlertasData}
 *     isLoadingEstoqueAlertas={isLoading}
 *     errorEstoqueAlertas={error}
 * />
 * ```
 */
const AlertsSection = ({
    pontoDesvios = null,
    isLoadingPontoDesvios,
    errorPontoDesvios = null,
    estoqueAlertas = null,
    isLoadingEstoqueAlertas,
    errorEstoqueAlertas = null,
    pontoTolerancia = '15 minutos',
    className = '',
}) => {
    // Determina se algum alerta está carregando
    const anyLoading = isLoadingPontoDesvios || isLoadingEstoqueAlertas;

    return (
        <section 
            id="alerts-section"
            className={`mt-8 ${className}`} 
            data-testid="alerts-section"
            aria-labelledby="alerts-section-title"
            aria-label="Seção de Alertas Operacionais"
        >
            {/* Título oculto para leitores de tela */}
            <h2 id="alerts-section-title" className="sr-only">
                Alertas Operacionais
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 pt-2 pb-2" role="list" aria-label="Alertas do dashboard">
                {/* Desvios de Ponto Recentes */}
                {isLoadingPontoDesvios ? (
                    <AlertSkeleton testId="ponto-desvios-skeleton" />
                ) : (
                    <PontoDesviosAlert
                        desvios={pontoDesvios}
                        isLoading={isLoadingPontoDesvios}
                        error={errorPontoDesvios}
                        tolerancia={pontoTolerancia}
                    />
                )}

                {/* Alertas Críticos de Farmácia */}
                {isLoadingEstoqueAlertas ? (
                    <AlertSkeleton testId="estoque-alertas-skeleton" />
                ) : (
                    <EstoqueAlertasAlert
                        alertas={estoqueAlertas}
                        isLoading={isLoadingEstoqueAlertas}
                        error={errorEstoqueAlertas}
                    />
                )}
            </div>
        </section>
    );
};

AlertsSection.propTypes = {
    /** Dados de desvios de ponto */
    pontoDesvios: PropTypes.array,
    
    /** Loading de desvios de ponto */
    isLoadingPontoDesvios: PropTypes.bool.isRequired,
    
    /** Erro de desvios de ponto */
    errorPontoDesvios: PropTypes.oneOfType([
        PropTypes.instanceOf(Error),
        PropTypes.string,
        PropTypes.oneOf([null]),
    ]),
    
    /** Dados de alertas de estoque */
    estoqueAlertas: PropTypes.array,
    
    /** Loading de alertas de estoque */
    isLoadingEstoqueAlertas: PropTypes.bool.isRequired,
    
    /** Erro de alertas de estoque */
    errorEstoqueAlertas: PropTypes.oneOfType([
        PropTypes.instanceOf(Error),
        PropTypes.string,
        PropTypes.oneOf([null]),
    ]),
    
    /** Tolerância de atraso para desvios de ponto */
    pontoTolerancia: PropTypes.string,
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
};

// Default props removidos - usando default parameters na função

// Memoizar componente para evitar re-renderizações desnecessárias
export default React.memo(AlertsSection, (prevProps, nextProps) => {
    // Comparação customizada para evitar re-renderizações desnecessárias
    return (
        prevProps.pontoDesvios === nextProps.pontoDesvios &&
        prevProps.isLoadingPontoDesvios === nextProps.isLoadingPontoDesvios &&
        prevProps.errorPontoDesvios === nextProps.errorPontoDesvios &&
        prevProps.estoqueAlertas === nextProps.estoqueAlertas &&
        prevProps.isLoadingEstoqueAlertas === nextProps.isLoadingEstoqueAlertas &&
        prevProps.errorEstoqueAlertas === nextProps.errorEstoqueAlertas &&
        prevProps.pontoTolerancia === nextProps.pontoTolerancia &&
        prevProps.className === nextProps.className
    );
});

